import {
  FREESIA_PROFILE_PROMPT,
  GARDEN_CORE_PROMPT,
} from '@/IsekaiRPG/modules/llm/garden/prompts';
import {
  DEFAULT_ST_OPENAI_FREQUENCY_PENALTY,
  DEFAULT_ST_OPENAI_MAX_TOKENS,
  DEFAULT_ST_OPENAI_MIN_P,
  DEFAULT_ST_OPENAI_PRESENCE_PENALTY,
  DEFAULT_ST_OPENAI_REPETITION_PENALTY,
  DEFAULT_ST_OPENAI_TEMPERATURE,
  DEFAULT_ST_OPENAI_TOP_A,
  DEFAULT_ST_OPENAI_TOP_K,
  DEFAULT_ST_OPENAI_TOP_P,
  type STPromptNode,
  type STPromptOrderBlock,
} from './base';

const DEFAULT_GARDEN_CORE_PROMPT_CONTENT = GARDEN_CORE_PROMPT.trim();
const DEFAULT_GARDEN_FLOWER_PROMPT_CONTENT = FREESIA_PROFILE_PROMPT.trim();

export const GARDEN_TEXT_LENGTH_PROMPT_IDENTIFIER = 'gardenTextLengthPrompt';
export const GARDEN_ADDITIONAL_REQUIREMENTS_PROMPT_IDENTIFIER = 'gardenAdditionalRequirementsPrompt';

export interface STPresetDefaults {
  enabled: boolean;
  prompts: STPromptNode[];
  prompt_order: STPromptOrderBlock[];
  use_sysprompt: boolean;
  custom_prompt_post_processing: '';
  temperature: number;
  topP: number;
  topK: number;
  topA: number;
  minP: number;
  repetitionPenalty: number;
  maxTokens: number;
  presencePenalty: number;
  frequencyPenalty: number;
}

export const DEFAULT_GARDEN_ST_PROMPTS: STPromptNode[] = [
  {
    identifier: 'gardenCorePrompt',
    name: 'Garden Core Prompt',
    role: 'system',
    content: DEFAULT_GARDEN_CORE_PROMPT_CONTENT,
    system_prompt: true,
    marker: true,
  },
  {
    identifier: 'worldInfoBefore',
    name: 'World Info (before)',
    role: 'system',
    content: '',
    system_prompt: true,
    marker: true,
  },
  {
    identifier: 'worldInfoAfter',
    name: 'World Info (after)',
    role: 'system',
    content: '',
    system_prompt: true,
    marker: true,
  },
  {
    identifier: 'chatHistory',
    name: 'Chat History',
    role: 'system',
    content: '',
    system_prompt: true,
    marker: true,
  },
  {
    identifier: 'gardenFlowerPrompt',
    name: 'Garden Flower Prompt',
    role: 'system',
    content: DEFAULT_GARDEN_FLOWER_PROMPT_CONTENT,
    system_prompt: true,
    marker: true,
  },
];

export const DEFAULT_GARDEN_ST_PROMPT_ORDER: STPromptOrderBlock[] = [
  {
    character_id: 100001,
    order: [
      { identifier: 'gardenCorePrompt', enabled: true },
      { identifier: 'worldInfoBefore', enabled: true },
      { identifier: 'worldInfoAfter', enabled: true },
      { identifier: 'chatHistory', enabled: true },
      { identifier: 'gardenFlowerPrompt', enabled: true },
    ],
  },
];

export const DEFAULT_COMPATIBILITY_ST_PROMPTS: STPromptNode[] = [
  {
    identifier: 'main',
    name: 'Main Prompt',
    role: 'system',
    content: '',
    system_prompt: true,
  },
  {
    identifier: 'gardenCorePrompt',
    name: 'Garden Core Prompt',
    role: 'system',
    content: DEFAULT_GARDEN_CORE_PROMPT_CONTENT,
    system_prompt: true,
    marker: true,
  },
  {
    identifier: 'worldInfoBefore',
    name: 'World Info (before)',
    role: 'system',
    content: '',
    system_prompt: true,
    marker: true,
  },
  {
    identifier: 'charDescription',
    name: 'Char Description',
    role: 'system',
    content: '',
    system_prompt: true,
    marker: true,
  },
  {
    identifier: 'charPersonality',
    name: 'Char Personality',
    role: 'system',
    content: '',
    system_prompt: true,
    marker: true,
  },
  {
    identifier: 'scenario',
    name: 'Scenario',
    role: 'system',
    content: '',
    system_prompt: true,
    marker: true,
  },
  {
    identifier: 'personaDescription',
    name: 'Persona Description',
    role: 'system',
    content: '',
    system_prompt: true,
    marker: true,
  },
  {
    identifier: 'nsfw',
    name: 'Auxiliary Prompt',
    role: 'system',
    content: '',
    system_prompt: true,
  },
  {
    identifier: 'worldInfoAfter',
    name: 'World Info (after)',
    role: 'system',
    content: '',
    system_prompt: true,
    marker: true,
  },
  {
    identifier: 'dialogueExamples',
    name: 'Chat Examples',
    role: 'system',
    content: '',
    system_prompt: true,
    marker: true,
  },
  {
    identifier: 'chatHistory',
    name: 'Chat History',
    role: 'system',
    content: '',
    system_prompt: true,
    marker: true,
  },
  {
    identifier: 'gardenFlowerPrompt',
    name: 'Garden Flower Prompt',
    role: 'system',
    content: DEFAULT_GARDEN_FLOWER_PROMPT_CONTENT,
    system_prompt: true,
    marker: true,
  },
  {
    identifier: 'jailbreak',
    name: 'Post-History Instructions',
    role: 'system',
    content: '',
    system_prompt: true,
  },
];

export const DEFAULT_COMPATIBILITY_ST_PROMPT_ORDER: STPromptOrderBlock[] = [
  {
    character_id: 100001,
    order: [
      { identifier: 'main', enabled: true },
      { identifier: 'gardenCorePrompt', enabled: true },
      { identifier: 'worldInfoBefore', enabled: true },
      { identifier: 'personaDescription', enabled: true },
      { identifier: 'charDescription', enabled: true },
      { identifier: 'charPersonality', enabled: true },
      { identifier: 'scenario', enabled: true },
      { identifier: 'nsfw', enabled: true },
      { identifier: 'worldInfoAfter', enabled: true },
      { identifier: 'dialogueExamples', enabled: true },
      { identifier: 'chatHistory', enabled: true },
      { identifier: 'gardenFlowerPrompt', enabled: true },
      { identifier: 'jailbreak', enabled: true },
    ],
  },
];

export function createGardenSTPresetDefaults(): STPresetDefaults {
  return {
    enabled: true,
    prompts: DEFAULT_GARDEN_ST_PROMPTS,
    prompt_order: DEFAULT_GARDEN_ST_PROMPT_ORDER,
    use_sysprompt: false,
    custom_prompt_post_processing: '',
    temperature: DEFAULT_ST_OPENAI_TEMPERATURE,
    topP: DEFAULT_ST_OPENAI_TOP_P,
    topK: DEFAULT_ST_OPENAI_TOP_K,
    topA: DEFAULT_ST_OPENAI_TOP_A,
    minP: DEFAULT_ST_OPENAI_MIN_P,
    repetitionPenalty: DEFAULT_ST_OPENAI_REPETITION_PENALTY,
    maxTokens: DEFAULT_ST_OPENAI_MAX_TOKENS,
    presencePenalty: DEFAULT_ST_OPENAI_PRESENCE_PENALTY,
    frequencyPenalty: DEFAULT_ST_OPENAI_FREQUENCY_PENALTY,
  };
}

export function createCompatibilitySTPresetDefaults(): STPresetDefaults {
  return {
    enabled: true,
    prompts: DEFAULT_COMPATIBILITY_ST_PROMPTS,
    prompt_order: DEFAULT_COMPATIBILITY_ST_PROMPT_ORDER,
    use_sysprompt: false,
    custom_prompt_post_processing: '',
    temperature: DEFAULT_ST_OPENAI_TEMPERATURE,
    topP: DEFAULT_ST_OPENAI_TOP_P,
    topK: DEFAULT_ST_OPENAI_TOP_K,
    topA: DEFAULT_ST_OPENAI_TOP_A,
    minP: DEFAULT_ST_OPENAI_MIN_P,
    repetitionPenalty: DEFAULT_ST_OPENAI_REPETITION_PENALTY,
    maxTokens: DEFAULT_ST_OPENAI_MAX_TOKENS,
    presencePenalty: DEFAULT_ST_OPENAI_PRESENCE_PENALTY,
    frequencyPenalty: DEFAULT_ST_OPENAI_FREQUENCY_PENALTY,
  };
}
